'use client';

import type { ShareComponentProps } from '@/types/share';
import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FaWhatsapp, FaLinkedin, FaCopy, FaCheck } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { cn } from '@/lib/utils';
import { copyToClipboard } from '@/utils/share';

export const ShareButtons = ({
  data,
  platforms = ['whatsapp', 'linkedin', 'x', 'copy'],
  className,
  vertical = false
}: ShareComponentProps) => {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback((platform: string) => {
    try {
      if (typeof window === 'undefined') return;
      
      const message = `${data.title}\n\n${data.description}`;
      const encodedMessage = encodeURIComponent(message);
      const encodedUrl = encodeURIComponent(data.url);

      const urls: Record<string, string> = {
        whatsapp: `https://wa.me/?text=${encodedMessage}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        x: `https://twitter.com/intent/tweet?text=${encodedMessage}`
      };

      if (platform in urls) {
        window.open(urls[platform], '_blank');
      }
    } catch (error) {
      console.error(`Error sharing to ${platform}:`, error);
    }
  }, [data]);

  const handleCopy = useCallback(async () => {
    const message = `${data.title}\n\n${data.description}`;
    const success = await copyToClipboard(message);
    
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [data]);

  return (
    <div className={cn('flex gap-2', vertical ? 'flex-col' : 'flex-row', className)}>
      {platforms.includes('whatsapp') && (
        <Button
          variant="outline"
          size="icon"
          onClick={() => handleShare('whatsapp')}
          className="hover:text-green-600"
        >
          <FaWhatsapp className="h-4 w-4" />
        </Button>
      )}
      {platforms.includes('linkedin') && (
        <Button
          variant="outline"
          size="icon"
          onClick={() => handleShare('linkedin')}
          className="hover:text-blue-600"
        >
          <FaLinkedin className="h-4 w-4" />
        </Button>
      )}
      {platforms.includes('x') && (
        <Button
          variant="outline"
          size="icon"
          onClick={() => handleShare('x')}
          className="hover:text-gray-800"
        >
          <FaXTwitter className="h-4 w-4" />
        </Button>
      )}
      {platforms.includes('copy') && (
        <Button
          variant="outline"
          size="icon"
          onClick={handleCopy}
          className="hover:text-blue-500"
        >
          {copied ? <FaCheck className="h-4 w-4 text-green-500" /> : <FaCopy className="h-4 w-4" />}
        </Button>
      )}
    </div>
  );
};
